import type { FaqEntry, StructuredData } from '~/types/types'

type SchemaQuestion = {
  '@type': 'Question'
  name: string
  acceptedAnswer: {
    '@type': 'Answer'
    text: string
  }
}

type FaqPageNode = {
  '@type': 'FAQPage'
  '@id'?: string
  mainEntity: SchemaQuestion[]
  [key: string]: unknown
}

/**
 * Registers a page's FAQ questions (from visible accordions) into a request-scoped
 * store and merges them into the page's existing structured data so that a single,
 * valid FAQPage (JSON-LD) is emitted per page.
 */
export function useFaqSchema() {
  // Request-scoped on the server; avoids cross-request leakage between SSR renders.
  const registeredBlocks = useState<SchemaQuestion[][]>('faq-schema-blocks', () => [])

  const stripHtml = (input: string | undefined | null): string =>
    (input ?? '')
      // remove <script>...</script> entirely (defense in depth; also stripped upstream)
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()

  const toQuestions = (entries: FaqEntry[] | undefined | null): SchemaQuestion[] =>
    (entries ?? [])
      .filter(entry => entry?.question)
      .map(entry => ({
        '@type': 'Question' as const,
        name: entry.question,
        acceptedAnswer: {
          '@type': 'Answer' as const,
          text: stripHtml(entry.answer_text),
        },
      }))

  /** Called by each visible FAQ block, in the visible order of its questions. */
  const registerFaqEntries = (entries: FaqEntry[] | undefined | null): void => {
    const questions = toQuestions(entries)
    if (questions.length === 0) return
    registeredBlocks.value = [...registeredBlocks.value, questions]
  }

  /**
   * Clears collected FAQ questions. Call at the start of a page's setup so that
   * client-side navigation between pages does not accumulate stale entries.
   */
  const resetFaqEntries = (): void => {
    registeredBlocks.value = []
  }

  /** All accordion questions collected across blocks, preserving registration order. */
  const collectedQuestions = computed<SchemaQuestion[]>(() => registeredBlocks.value.flat())

  const isFaqPageNode = (node: unknown): node is FaqPageNode =>
    !!node && typeof node === 'object' && (node as { '@type'?: unknown })['@type'] === 'FAQPage'

  const dedupeByName = (questions: SchemaQuestion[]): SchemaQuestion[] => {
    const seen = new Set<string>()
    return questions.filter(question => {
      const key = question.name.trim().toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }

  /**
   * Returns the structured data with a single merged FAQPage.
   * - If the CMS graph already contains a FAQPage, the collected accordion questions
   *   are appended to it (its `@id`/graph link is preserved).
   * - Otherwise a FAQPage is created and, when a WebPage `@id` is present, linked to it
   *   via `mainEntityOfPage`.
   */
  const buildMergedStructuredData = (structuredData: StructuredData | undefined): unknown => {
    const questions = collectedQuestions.value

    // Normalize: Directus may return a parsed object or (per its type) a JSON string.
    let data: unknown = structuredData
    if (typeof structuredData === 'string') {
      try {
        data = JSON.parse(structuredData)
      } catch {
        data = null
      }
    }

    // No usable structured data: emit a standalone FAQPage only if we have questions.
    if (!data || typeof data !== 'object') {
      if (questions.length === 0) return data ?? null
      return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: dedupeByName(questions),
      }
    }

    // Deep clone to avoid mutating the reactive CMS payload.
    const cloned = JSON.parse(JSON.stringify(data)) as Record<string, unknown>

    if (questions.length === 0) return cloned

    const graph = Array.isArray((cloned as { '@graph'?: unknown })['@graph'])
      ? ((cloned as { '@graph': unknown[] })['@graph'] as unknown[])
      : null

    if (graph) {
      const existingFaq = graph.find(isFaqPageNode) as FaqPageNode | undefined
      if (existingFaq) {
        const base = Array.isArray(existingFaq.mainEntity) ? existingFaq.mainEntity : []
        existingFaq.mainEntity = dedupeByName([...base, ...questions])
        return cloned
      }

      // No FAQPage in graph: create one and link to the WebPage if present.
      const webPage = graph.find(
        node =>
          !!node &&
          typeof node === 'object' &&
          (node as { '@type'?: unknown })['@type'] === 'WebPage',
      ) as { '@id'?: string } | undefined

      const faqNode: FaqPageNode = {
        '@type': 'FAQPage',
        mainEntity: dedupeByName(questions),
      }
      if (webPage?.['@id']) {
        faqNode['@id'] = `${webPage['@id'].replace(/#.*$/, '')}#faq`
        faqNode.mainEntityOfPage = { '@id': webPage['@id'] }
      }
      graph.push(faqNode)
      return cloned
    }

    // Structured data is a single node (no @graph).
    if (isFaqPageNode(cloned)) {
      const base = Array.isArray(cloned.mainEntity) ? cloned.mainEntity : []
      cloned.mainEntity = dedupeByName([...base, ...questions])
      return cloned
    }

    // Single non-FAQPage node: wrap both into a @graph.
    return {
      '@context': (cloned as { '@context'?: unknown })['@context'] ?? 'https://schema.org',
      '@graph': [cloned, { '@type': 'FAQPage', mainEntity: dedupeByName(questions) }],
    }
  }

  return {
    registerFaqEntries,
    resetFaqEntries,
    collectedQuestions,
    buildMergedStructuredData,
  }
}
